// Copyright (c) 2026 TRIVONT IT Private Limited. All rights reserved.

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { setAppTimezone } from '../utils/timezone';

const AuthContext = createContext(null);

function applyTimezone(userData) {
  // Set the global timezone from the user's tenant
  const tz = userData?.tenant_timezone || 'Asia/Kolkata';
  setAppTimezone(tz);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const stored = localStorage.getItem('itsm_user');
    if (token && stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        applyTimezone(parsed);
        // Verify token is still valid and get fresh user data
        authAPI.me().then(res => {
          setUser(res.data);
          applyTimezone(res.data);
          localStorage.setItem('itsm_user', JSON.stringify(res.data));
        }).catch(() => {
          localStorage.clear();
          setUser(null);
        });
      } catch {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((userData, tokens) => {
    setUser(userData);
    applyTimezone(userData);
    localStorage.setItem('itsm_user', JSON.stringify(userData));
    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
    if (userData.tenant) {
      localStorage.setItem('tenant_id', typeof userData.tenant === 'object' ? userData.tenant.id : userData.tenant);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const refresh = localStorage.getItem('refresh_token');
      if (refresh) await authAPI.logout(refresh);
    } catch {}
    setUser(null);
    localStorage.clear();
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
